import {
  COUNTRY_TO_CONTINENT,
  isCountryContinentSlugConflict,
  NEAREST_CONTINENTS,
  normalizeRegionCode,
} from "./regionMaps";

export type RegionResolutionSource =
  | "legacy_default"
  | "country"
  | "continent"
  | "nearest_continent"
  | "global_default";

export interface ResolvedAIEndpoint {
  url: string;
  countryCode: string;
  source: RegionResolutionSource;
}

export interface AIEndpointContext {
  userCountryCode?: string | null;
  teamCountryCode?: string | null;
}

export function isAIRegionRoutingEnabled(): boolean {
  return process.env.VUE_APP_AI_REGION_ROUTING_ENABLED === "on";
}

export function getGlobalAIUrl(): string {
  return (process.env.VUE_APP_AI_URL || "").trim();
}

function ensureNonEmptyUrl(url: string, fallback: string): string {
  const trimmed = (url || "").trim();
  return trimmed || fallback;
}

export function parseRegionEndpoints(
  rawJson = process.env.VUE_APP_AI_REGION_ENDPOINTS
): Record<string, string> {
  if (!rawJson || typeof rawJson !== "string") return {};

  try {
    const parsed = JSON.parse(rawJson) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    const endpoints: Record<string, string> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
      if (typeof value !== "string") return;
      const normalizedKey = normalizeRegionCode(key);
      const normalizedUrl = value.trim();
      if (!normalizedKey || !normalizedUrl) return;
      endpoints[normalizedKey] = normalizedUrl;
    });
    return endpoints;
  } catch {
    return {};
  }
}

export function resolveAIEndpoint(
  regionCode: string,
  endpoints: Record<string, string> = parseRegionEndpoints(),
  globalDefault: string = getGlobalAIUrl()
): ResolvedAIEndpoint {
  const region = normalizeRegionCode(regionCode) ?? "us";
  const fallbackUrl = globalDefault;

  if (
    endpoints[region] &&
    !isCountryContinentSlugConflict(region)
  ) {
    return {
      url: ensureNonEmptyUrl(endpoints[region], fallbackUrl),
      countryCode: region,
      source: "country",
    };
  }

  const continent = COUNTRY_TO_CONTINENT[region];
  if (continent && endpoints[continent]) {
    return {
      url: ensureNonEmptyUrl(endpoints[continent], fallbackUrl),
      countryCode: region,
      source: "continent",
    };
  }

  if (continent) {
    for (const near of NEAREST_CONTINENTS[continent] ?? []) {
      if (endpoints[near]) {
        return {
          url: ensureNonEmptyUrl(endpoints[near], fallbackUrl),
          countryCode: region,
          source: "nearest_continent",
        };
      }
    }
  }

  return {
    url: ensureNonEmptyUrl(fallbackUrl, fallbackUrl),
    countryCode: region,
    source: "global_default",
  };
}

export function resolveAIEndpointFromContext(
  context?: AIEndpointContext,
  endpoints: Record<string, string> = parseRegionEndpoints(),
  globalDefault: string = getGlobalAIUrl()
): ResolvedAIEndpoint {
  const region =
    normalizeRegionCode(context?.userCountryCode) ??
    normalizeRegionCode(context?.teamCountryCode) ??
    "us";

  return resolveAIEndpoint(region, endpoints, globalDefault);
}

export function getAIEndpointResolved(
  context?: AIEndpointContext
): ResolvedAIEndpoint {
  const globalDefault = getGlobalAIUrl();

  if (!isAIRegionRoutingEnabled()) {
    return {
      url: ensureNonEmptyUrl(globalDefault, globalDefault),
      countryCode: "us",
      source: "legacy_default" as const,
    };
  }

  return resolveAIEndpointFromContext(context);
}
